import { useCallback, useEffect, useState } from 'react';

import { get } from '../../api';
import { useGlobalStore } from '../state/globalStore';
import { ENTITY_TO_PATH_MAP, REGISTRANT_STATUS } from '../utils/constants';
import { getEntityCookiePassword } from '../utils/helper';
import { EVENT_STATUS } from '../utils/types';

type RegistrantStatusResult = {
  loading: boolean;
  called: boolean;
  initialDataLoaded: boolean;
  data: {
    isBlocked: boolean;
    /**
     * If valid registrant is false it means.
     * 1. The lead id given is invalid
     * 2. The lead id given is a registrant which was deleted.
     */
    isValidRegistrant: boolean;
  };
};

const useFetchStatus = () => {
  const entityId = useGlobalStore((store) => store.apiEntityId);
  const entityType = useGlobalStore((store) => store.entityType);

  const [state, setState] = useState<RegistrantStatusResult>({
    loading: false,
    called: false,
    initialDataLoaded: false,
    data: { isBlocked: false, isValidRegistrant: false },
  });

  const fetcher = useCallback(
    async (leadId?: string) => {
      if (!entityId || !entityType || !leadId || leadId.length < 1) {
        return;
      }
      let data: any;
      let error: any;
      try {
        setState((state) => ({
          ...state,
          loading: true,
          called: true,
        }));
        const entityPassword = getEntityCookiePassword(entityId, entityType || '');
        data = await get(
          `/lead_capture/${ENTITY_TO_PATH_MAP[entityType]}/${entityId}/registrants/${leadId}?fields=${REGISTRANT_STATUS}&password=${entityPassword}`,
        );
      } catch (err) {
        error = err;
      } finally {
        /** If any error occoured except the 404, then persist the previous state. */
        if (error && error?.status !== 404) {
          return;
        }
        setState((state) => ({
          ...state,
          loading: false,
          initialDataLoaded: true,
          data: {
            isBlocked: Boolean(data?.isBlocked),
            isValidRegistrant: Boolean(data) && error?.status !== 404,
          },
        }));
      }
    },
    [entityId, entityType],
  );

  return [fetcher, state] as const;
};

export const useRegistrantStatus = (
  leadId: string,
  status: string | null,
): RegistrantStatusResult => {
  const [pollCount, setPollCount] = useState(0);

  const [fetch, state] = useFetchStatus();

  useEffect(() => {
    if (status === EVENT_STATUS.ENDED) return;

    const pollBlockStatusInterval = 10000;
    /**
     * User may get blocked during the live event, hence we need to continously poll the block status.
     */
    const poller = setInterval(() => {
      setPollCount((count) => count + 1);
    }, pollBlockStatusInterval);

    return () => {
      clearInterval(poller);
    };
  }, [status]);

  useEffect(() => {
    if (status === EVENT_STATUS.ENDED) return;
    fetch(leadId);
  }, [pollCount, leadId, fetch, status]);

  return state;
};
